import { fetchImage } from '/src/utils/images.js';

const MODELS_URL = '/models';

/**
 * https://vladmandic.github.io/human/typedoc/interfaces/Config.html
 */
const defaultOptions = {
	modelBasePath: MODELS_URL,
	debug: true,
	cacheSensitivity: 0,
	deallocate: true,
	minConfidence: 0.3,
	warmup: 'face',
	filter: {
		enabled: true,
		equalization: true,
		width: 640,
	},
	face: {
		enabled: true,
		detector: {
			rotation: true,
			skipFrames: 1,
			maxDetected: 1,
			iouThreshold: 0.1,
			// no crop tensor per face: nothing reads it, and Human never
			// disposes it, so every detect() would leak one
			return: false,
		},
		mesh: {
			enabled: true,
			keepInvalid: false,
		},
		description: {
			enabled: true,
			skipFrames: 1,
		},
		iris: { enabled: false },
		emotion: { enabled: false },
		attention: { enabled: false },
		antispoof: { enabled: true, modelPath: 'antispoof.json' },
		liveness: { enabled: true, modelPath: 'liveness.json' },
		gear: { enabled: true, return: true, modelPath: 'gear.json' },
	},
	gesture: { enabled: false },
	body: { enabled: false },
	hand: { enabled: false },
	segmentation: { enabled: false },
	object: { enabled: false },
};

let human;
let humanReady = false;

export async function initHuman () {
	if (humanReady) return;
	const { default: Human } = await import('@vladmandic/human');
	human = new Human(defaultOptions);
	await human.load('face');
	await human.warmup();
	humanReady = true;
}

export async function detectSingleFace (source) {
	const result = await human.detect(source);

	if (result.face.length <= 0) {
		return null;
	}

	const face = result.face[0];

	const { embedding: descriptor } = face;

	return descriptor;
}

function getDescriptors (descriptors) {
	const result = [];

	descriptors.forEach((descriptor, index) => {
		// Ensure descriptor and descriptor.descriptors exist and are valid
		if (!descriptor || !descriptor.descriptors || !Array.isArray(descriptor.descriptors)) {
			console.warn('[getDescriptors] Invalid descriptor structure:', descriptor);
			return;
		}

		const firstDescriptor = descriptor.descriptors[0];

		// Validate that the first descriptor is a valid array
		if (!firstDescriptor || !Array.isArray(firstDescriptor) || firstDescriptor.length === 0) {
			console.warn('[getDescriptors] Invalid first descriptor:', firstDescriptor);
			return;
		}

		result.push({
			descriptor: firstDescriptor,
			originalIndex: index,
			label: descriptor.label,
		});
	});

	return result;
}

export function findSimilarity (lookupDescriptor, knownDescriptors, confidence = 0.5) {
	console.log('🎥 [findSimilarity] lookupDescriptor length', lookupDescriptor?.length);
	console.log('🎥 [findSimilarity] knownDescriptors count', knownDescriptors?.length);
	console.log('🎥 [findSimilarity] confidence', confidence);

	// Validate inputs before calling human.match.find
	if (!lookupDescriptor || !Array.isArray(lookupDescriptor) || lookupDescriptor.length === 0) {
		console.warn('[findSimilarity] Invalid lookupDescriptor:', lookupDescriptor);
		return null;
	}

	if (!knownDescriptors || !Array.isArray(knownDescriptors) || knownDescriptors.length === 0) {
		console.warn('[findSimilarity] Invalid or empty knownDescriptors:', knownDescriptors);
		return null;
	}

	// Extract and validate descriptors while preserving indices and labels
	const validDescriptorsWithIndices = getDescriptors(knownDescriptors);

	if (!validDescriptorsWithIndices || validDescriptorsWithIndices.length === 0) {
		console.warn('[findSimilarity] No valid descriptors found in knownDescriptors');
		return null;
	}

	// Extract just the descriptor arrays for matching
	const descriptorsForMatching = validDescriptorsWithIndices.map(item => item.descriptor);

	console.log('[findSimilarity] descriptorsForMatching count:', descriptorsForMatching.length);

	const match = human.match.find(lookupDescriptor, descriptorsForMatching);

	console.log('[DEBUG findSimilarity] Match', match);

	if (!match || match.index === -1) {
		console.log('[DEBUG findSimilarity] No match found.', match);
		return null;
	}

	console.log(`[DEBUG findSimilarity] Faces are ${100 * match.similarity}% similar`);

	if (match.similarity < confidence) {
		console.log(`[DEBUG findSimilarity] Match found, but similarity ${match.similarity} is below threshold ${confidence}`);
		return null;
	}

	// Use the preserved label from our validated descriptors
	const matchedDescriptor = validDescriptorsWithIndices[match.index];
	console.log('[DEBUG findSimilarity] Matched label:', matchedDescriptor.label);

	return matchedDescriptor.label;
}

/**
 * Embedding of the first photo with a detectable face. Only one descriptor per
 * employee is ever matched (see getDescriptors), so once a photo yields one the
 * remaining photos are neither fetched nor run through the model.
 */
async function getFirstFaceDescriptor (urls = []) {
	for (const url of urls) {
		try {
			const image = await fetchImage(url);
			const result = await human.detect(image);

			if (result.face.length <= 0) {
				console.warn(`[getFirstFaceDescriptor] ⚠️ No face detected in successfully loaded image: ${url}`);
				continue;
			}

			// plain copy: nothing from the Human result is kept around
			return Array.from(result.face[0].embedding);
		} catch (error) {
			console.error(error);
		}
	}

	return null;
}

/**
 * The face entry the store keeps for an employee: its id and at most one
 * descriptor. The rest of the employee payload lives in the employees store.
 */
export async function formatFace (face) {
	const data = { id: face?.id, descriptors: [] };

	if (face?.createdFaceIds?.length) {
		try {
			const descriptor = await getFirstFaceDescriptor(face.createdFaceIds);

			if (descriptor) {
				data.descriptors = [descriptor];
			}
		} catch (error) {
			console.error(`[formatFace] Employee ${face?.id}: Failed to process faces:`, error);
		}
	}

	return data;
}

export { human };
